import { useMemo } from 'react'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import { FormattedMessage, useIntl } from 'react-intl'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Typography from '@mui/material/Typography'
import { map } from 'lodash'
import FormikTextField from '../../../../shared/components/formik/FormikTextField'
import FormikSelectField from '../../../../shared/components/formik/FormikSelectField'

const generateDefaultName = () => {
  const now = new Date()
  const year = now.getFullYear()
  const month = String(now.getMonth() + 1).padStart(2, '0')
  const day = String(now.getDate()).padStart(2, '0')
  const hours = String(now.getHours()).padStart(2, '0')
  const minutes = String(now.getMinutes()).padStart(2, '0')
  return `D${year}${month}${day}-${hours}${minutes}`
}

const DeploymentCreateForm = ({ nodes, createDeployment, onSuccess, onCancel }) => {
  const intl = useIntl()

  const nodeOptions = useMemo(() => {
    return map(nodes, (node) => ({
      value: node.id,
      label: node.name
    }))
  }, [nodes])

  const defaultNodeIds = useMemo(() => {
    return map(nodes, 'id')
  }, [nodes])

  const validationSchema = Yup.object({
    name: Yup.string().required(intl.formatMessage({ id: 'login.error.required' })),
    nodeIds: Yup.array()
  })

  const formik = useFormik({
    initialValues: {
      name: generateDefaultName(),
      nodeIds: defaultNodeIds
    },
    validationSchema,
    enableReinitialize: true,
    onSubmit: (values, { setSubmitting, setStatus }) => {
      const deploymentData = {
        deployment: {
          name: values.name,
          nodeIds: values.nodeIds
        }
      }

      createDeployment(deploymentData)
        .then(() => {
          onSuccess()
        })
        .catch((err) => {
          setStatus(err.message || 'Error creating deployment')
        })
        .finally(() => {
          setSubmitting(false)
        })
    }
  })

  return (
    <Box component="form" onSubmit={formik.handleSubmit}>
      <FormikTextField
        formik={formik}
        name="name"
        label={<FormattedMessage id="deployments.create.nameLabel" />}
        autoFocus
      />

      <Box sx={{ mt: 2 }}>
        <FormikSelectField
          formik={formik}
          name="nodeIds"
          label={<FormattedMessage id="deployments.create.nodesLabel" />}
          options={nodeOptions}
          multiple
        />
      </Box>

      {formik.status && (
        <Typography color="error" sx={{ mt: 2 }}>
          {formik.status}
        </Typography>
      )}

      <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
        <Button
          variant="outlined"
          onClick={onCancel}
          disabled={formik.isSubmitting}
        >
          <FormattedMessage id="app.actions.cancel.label" />
        </Button>
        <Button
          color="primary"
          variant="contained"
          type="submit"
          disabled={formik.isSubmitting}
        >
          <FormattedMessage id="deployments.create.submitButton" />
        </Button>
      </Box>
    </Box>
  )
}

export default DeploymentCreateForm
